import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import Logo from "../components/common/Logo";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import SuccessMessage from "../components/common/SuccessMessage";
import CategoryManager from "../components/items/CategoryManager";
import ItemManager from "../components/items/ItemManager";
import { getCategories } from "../services/itemService";

const ItemManagement = () => {
  const { logout } = useAuth();
  const [activeTab, setActiveTab] = useState("categories");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const categoriesData = await getCategories();
      setCategories(categoriesData);
    } catch (error) {
      setError("Failed to load categories");
      console.error("Error loading categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryUpdate = () => {
    loadCategories();
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setError("");
    setTimeout(() => setSuccess(""), 5000);
  };

  const showError = (message) => {
    setError(message);
    setSuccess("");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="page-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <Logo size="medium" />
              <div>
                <h1 className="page-title">Item Management</h1>
                <p className="page-subtitle">Manage categories and items</p>
              </div>
            </div>
            <div className="header-actions">
              <button
                onClick={() => window.history.back()}
                className="btn btn-secondary"
              >
                ← Back to Dashboard
              </button>
              <button onClick={logout} className="btn btn-outline">
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        <div className="container">
          {/* Messages */}
          {error && <ErrorMessage message={error} />}
          {success && <SuccessMessage message={success} />}

          {/* Tab Navigation */}
          <div className="tab-navigation">
            <button
              className={`tab-button ${activeTab === "categories" ? "active" : ""}`}
              onClick={() => setActiveTab("categories")}
            >
              📁 Categories
            </button>
            <button
              className={`tab-button ${activeTab === "items" ? "active" : ""}`}
              onClick={() => setActiveTab("items")}
            >
              🛒 Items
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === "categories" && (
              <CategoryManager
                categories={categories}
                onCategoryUpdate={handleCategoryUpdate}
                onSuccess={showSuccess}
                onError={showError}
              />
            )}
            {activeTab === "items" && (
              <ItemManager
                categories={categories}
                onSuccess={showSuccess}
                onError={showError}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ItemManagement;
